import { useSyncExternalStore } from "react";
import type { RdScheduleAppointment } from "../../../shared/rd-schedule";

// The RD's private note on a visit (Healthie's appointment note). Saving it
// answers 202 before the worker writes to Healthie, so a read just after can
// still return the old note. This holds the note the RD saved, per visit, and
// the panel shows it with "Saving to Healthie…" until a read agrees or the
// window runs out. Relative imports only: `tsx --test` loads this.

/** A visit's note is editable while it is scheduled or checked in (the server's actionable.edit). */
const EDITABLE_STATUSES: ReadonlySet<string> = new Set(["scheduled", "checked_in"]);

export function isNoteEditable(
  appointment: Pick<RdScheduleAppointment, "status">
): boolean {
  return EDITABLE_STATUSES.has(appointment.status);
}

/** How long a saved note is shown ahead of Healthie. */
export const PENDING_NOTE_WINDOW_MS = 2 * 60 * 1000;
/** While a save is pending and the panel is open, the note is read this often... */
export const NOTE_POLL_EVERY_MS = 3000;
/** ...for this long. */
export const NOTE_POLL_FOR_MS = 30_000;

export interface PendingNote {
  note: string;
  savedAt: number;
}

export type PendingNoteStatus = "none" | "saving" | "settled";

const sameNote = (a: string | null, b: string | null): boolean =>
  (a ?? "").trim() === (b ?? "").trim();

/**
 * Where a pending save stands against the note a read returned (`undefined`
 * while there is no read yet): settled once Healthie returns it or the window
 * has passed, saving until then.
 */
export function pendingNoteStatus(
  pending: PendingNote | null,
  read: string | null | undefined,
  now: number
): PendingNoteStatus {
  if (pending === null) return "none";

  if (now - pending.savedAt >= PENDING_NOTE_WINDOW_MS) return "settled";

  return read !== undefined && sameNote(read, pending.note) ? "settled" : "saving";
}

export interface NoteLockPlan {
  status: PendingNoteStatus;
  /** Re-read the options every NOTE_POLL_EVERY_MS: only while a save is still pending. */
  poll: boolean;
  /** Milliseconds until the pending window ends (re-render then); null when not saving. */
  settleInMs: number | null;
}

/** What an open Edit dialog does about a pending save: poll while locked, wake when the window ends. */
export function noteLockPlan(
  pending: PendingNote | null,
  read: string | null | undefined,
  now: number
): NoteLockPlan {
  const status = pendingNoteStatus(pending, read, now);

  if (status !== "saving" || pending === null) {
    return { status, poll: false, settleInMs: null };
  }

  return {
    status,
    poll: true,
    settleInMs: Math.max(0, pending.savedAt + PENDING_NOTE_WINDOW_MS - now),
  };
}

const pendingByVisit = new Map<string, PendingNote>();
const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach((listener) => listener());

export function setPendingNote(
  appointmentId: string,
  note: string,
  now: number = Date.now()
): void {
  pendingByVisit.set(appointmentId, { note, savedAt: now });
  notify();
}

export function clearPendingNote(appointmentId: string): void {
  if (pendingByVisit.delete(appointmentId)) notify();
}

export function getPendingNote(appointmentId: string): PendingNote | null {
  return pendingByVisit.get(appointmentId) ?? null;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** The visit's pending save, re-rendering when it is set or cleared. */
export function usePendingNote(appointmentId: string): PendingNote | null {
  const snapshot = () => getPendingNote(appointmentId);

  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
